import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowLeft, Check, Phone, MessageCircle, Download } from 'lucide-react';
import { ENQUIRY, CONTACT } from '@/content';
import { track, utmParams } from '@/lib/track';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'metroone_enquiry_draft';
const BROCHURE_URL = '/metro-one-brochure.pdf';

const empty = { name: '', mobile: '', interest: '', furnishing: '', timeline: '', visitDate: '', visitTime: '', consent: false, company: '' };

export default function Enquiry({ open, preset, onClose }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const submittedRef = useRef(false);
  const downloadFired = useRef(false);
  const brochure = !!preset?.brochure;

  const triggerBrochureDownload = (placement) => {
    const a = document.createElement('a');
    a.href = BROCHURE_URL;
    a.download = 'Metro-One-Navade-Brochure.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    track('brochure_download', { placement });
    track('download_brochure', { placement });
  };

  useEffect(() => {
    if (open) {
      const draft = (() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; } })();
      setForm({ ...empty, ...draft, ...(preset?.interest ? { interest: preset.interest } : {}) });
      setStep(0);
      setDone(false);
      setFailed(false);
      setErrors({});
      submittedRef.current = false;
      downloadFired.current = false;
      track('form_open', { preset: preset?.interest || null, brochure: !!preset?.brochure });
    }
  }, [open, preset]);

  // brochure-gated flow: start the download automatically once the enquiry is submitted
  useEffect(() => {
    if (done && brochure && !downloadFired.current) {
      downloadFired.current = true;
      triggerBrochureDownload('enquiry_gate_auto');
    }
  }, [done, brochure]);

  useEffect(() => {
    if (!done) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch {}
    }
  }, [form, done]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validStep = () => {
    const e = {};
    if (step === 0) {
      if (form.name.trim().length < 2) e.name = 'Please enter your name';
      const m = form.mobile.replace(/\D/g, '').slice(-10);
      if (!/^[6-9]\d{9}$/.test(m)) e.mobile = 'Enter a valid 10-digit mobile number';
    }
    if (step === 1 && !form.interest) e.interest = 'Choose one option';
    if (step === 2 && !form.timeline) e.timeline = 'Choose your timeline';
    if (step === 3 && !form.visitTime) e.visitTime = 'Pick a preferred time';
    if (step === 4 && !form.consent) e.consent = 'Consent is required to contact you';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validStep()) return;
    track('form_step', { step: step + 2, of: 5 });
    setStep((s) => Math.min(4, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (submitting || submittedRef.current) return; // duplicate-submission protection
    if (!validStep()) return;
    setSubmitting(true);
    setFailed(false);
    try {
      const res = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          mobile: form.mobile,
          configuration: form.interest,
          furnishing: form.furnishing || null,
          timeline: form.timeline,
          visit_date: form.visitDate || null,
          visit_time: form.visitTime,
          consent: form.consent,
          source_page: window.location.pathname,
          utm: utmParams(),
          company: form.company,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Submission failed');
      }
      submittedRef.current = true;
      track('lead_submit', { configuration: form.interest, timeline: form.timeline });
      track('form_submit', { configuration: form.interest, timeline: form.timeline });
      localStorage.removeItem(STORAGE_KEY);
      setDone(true);
    } catch (err) {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  const waText = encodeURIComponent(
    `Hi, I’m ${form.name || 'a home seeker'}. I just booked a site visit for Codename Metro One, Navade (${form.interest || '1/2 BHK'}${form.visitDate ? `, preferred: ${form.visitDate} ${form.visitTime}` : ''}). Please confirm.`
  );

  const steps = ['You', 'Home', 'Timeline', 'Visit', 'Confirm'];

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[80] bg-[#1C1D21]/55 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            data-testid="enquiry-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Book a site visit"
            className="safe-bottom fixed inset-x-0 bottom-0 z-[81] flex max-h-[94dvh] flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:inset-0 sm:m-auto sm:h-fit sm:max-h-[92dvh] sm:max-w-lg sm:rounded-3xl"
            initial={{ y: '100%', opacity: 0.6 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
          >
            {/* header */}
            <div className="flex items-center justify-between border-b border-[#E4E6EB] px-6 py-4">
              <div>
                <p className="eyebrow !text-[10px]">{ENQUIRY.eyebrow}</p>
                <h2 className="font-display text-lg font-extrabold uppercase tracking-tight text-[#1C1D21]">
                  {done ? (brochure ? 'Your Brochure Is Ready' : 'Visit Request Received') : (brochure ? 'Get the Brochure' : 'Book a Site Visit')}
                </h2>
              </div>
              <button data-testid="enquiry-close-btn" aria-label="Close enquiry form" onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E4E6EB]">
                <X size={17} />
              </button>
            </div>

            {!done && (
              <div className="flex items-center gap-2 px-6 pt-5" aria-label={`Step ${step + 1} of 5: ${steps[step]}`}>
                {steps.map((s, i) => (
                  <div key={s} className="flex flex-1 flex-col gap-1.5">
                    <span className={`h-1.5 rounded-full transition-colors duration-300 ${i <= step ? 'bg-[#C92F4B]' : 'bg-[#E4E6EB]'}`} />
                    <span className={`text-[9px] font-bold uppercase tracking-[0.14em] ${i === step ? 'text-[#A41225]' : 'text-[#646A72]'}`}>{s}</span>
                  </div>
                ))}
              </div>
            )}

            <form
              data-testid="enquiry-form"
              noValidate
              onSubmit={(e) => { e.preventDefault(); if (step < 4) next(); else submit(); }}
              className="flex min-h-0 flex-1 flex-col"
            >
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {done ? (
                <motion.div data-testid="enquiry-success" className="flex flex-col items-center py-6 text-center" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}>
                  <motion.span
                    className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-[#C92F4B] text-[#C92F4B]"
                    initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 12, delay: 0.1 }}
                  >
                    <Check size={36} strokeWidth={3} />
                  </motion.span>
                  <h3 className="mt-5 font-display text-2xl font-extrabold uppercase tracking-tight text-[#1C1D21]">
                    Thank you, {form.name.split(' ')[0]}.
                  </h3>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-[#404145]">
                    Your site-visit request for <strong>{form.interest}</strong>{form.visitDate ? ` on ${form.visitDate} (${form.visitTime})` : ''} is with our team. We’ll call you on <strong>+91 {form.mobile.replace(/\D/g, '').slice(-10)}</strong> to confirm.
                  </p>
                  {brochure && (
                    <p data-testid="success-brochure-note" className="mt-2 max-w-sm text-sm font-semibold leading-relaxed text-[#C92F4B]">
                      Your brochure download has started automatically.
                    </p>
                  )}
                  <div className="mt-6 grid w-full gap-3">
                    {brochure && (
                      <button
                        type="button"
                        data-testid="success-brochure-btn"
                        onClick={() => triggerBrochureDownload('enquiry_gate_manual')}
                        className="btn-primary w-full"
                      >
                        <Download size={16} /> Download Brochure Again
                      </button>
                    )}
                    <a data-testid="success-call-btn" href={`tel:${CONTACT.phone}`} onClick={() => track('call_click', { placement: 'success' })} className={brochure ? 'btn-ghost w-full' : 'btn-primary w-full'}>
                      <Phone size={16} /> Call Now — {CONTACT.phoneDisplay}
                    </a>
                    <a
                      data-testid="success-whatsapp-btn"
                      href={`https://wa.me/${CONTACT.whatsapp}?text=${waText}`}
                      target="_blank" rel="noopener noreferrer"
                      onClick={() => track('whatsapp_click', { placement: 'success' })}
                      className="btn-ghost w-full !border-[#25D366]/50 !text-[#128C4B]"
                    >
                      <MessageCircle size={16} /> Confirm on WhatsApp
                    </a>
                  </div>
                </motion.div>
              ) : (
                <AnimatePresence mode="wait">
                  <motion.div key={step} initial={{ opacity: 0, x: 26 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -26 }} transition={{ duration: 0.28 }}>
                    {step === 0 && (
                      <div className="space-y-5">
                        <div>
                          <label htmlFor="enq-name" className="text-sm font-bold text-[#1C1D21]">Your name</label>
                          <input
                            id="enq-name" name="name" data-testid="enquiry-name-input" type="text" autoComplete="name" required
                            aria-invalid={!!errors.name} aria-describedby={errors.name ? 'enq-name-error' : undefined}
                            value={form.name} onChange={(e) => set('name', e.target.value)}
                            onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 320)}
                            placeholder="e.g. Rohan Patil"
                            className="mt-2 w-full rounded-xl border border-[#E4E6EB] bg-[#FAF9F6] px-4 py-3.5 text-base outline-none transition-colors focus:border-[#C92F4B]"
                          />
                          {errors.name && <p id="enq-name-error" role="alert" data-testid="enquiry-name-error" className="mt-1.5 text-xs font-semibold text-[#A41225]">{errors.name}</p>}
                        </div>
                        <div>
                          <label htmlFor="enq-mobile" className="text-sm font-bold text-[#1C1D21]">Mobile number</label>
                          <div className="mt-2 flex overflow-hidden rounded-xl border border-[#E4E6EB] bg-[#FAF9F6] transition-colors focus-within:border-[#C92F4B]">
                            <span className="flex items-center border-r border-[#E4E6EB] px-4 text-sm font-bold text-[#404145]">+91</span>
                            <input
                              id="enq-mobile" name="mobile" data-testid="enquiry-mobile-input" type="tel" inputMode="numeric" autoComplete="tel-national" maxLength={12} required
                              aria-invalid={!!errors.mobile} aria-describedby={errors.mobile ? 'enq-mobile-error' : undefined}
                              value={form.mobile} onChange={(e) => set('mobile', e.target.value.replace(/[^\d\s-]/g, ''))}
                              onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 320)}
                              placeholder="98765 43210"
                              className="w-full bg-transparent px-4 py-3.5 text-base outline-none"
                            />
                          </div>
                          {errors.mobile && <p id="enq-mobile-error" role="alert" data-testid="enquiry-mobile-error" className="mt-1.5 text-xs font-semibold text-[#A41225]">{errors.mobile}</p>}
                        </div>
                        <input type="text" name="company" value={form.company} onChange={(e) => set('company', e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                      </div>
                    )}

                    {step === 1 && (
                      <div>
                        <p className="text-sm font-bold text-[#1C1D21]">What are you looking for?</p>
                        <div className="mt-4 grid gap-3">
                          {ENQUIRY.interests.map((i) => (
                            <button
                              type="button"
                              key={i} data-testid={`enquiry-interest-${i.replace(/\s/g, '-').toLowerCase()}`}
                              onClick={() => set('interest', i)}
                              aria-pressed={form.interest === i}
                              className={`flex min-h-[56px] items-center justify-between rounded-xl border px-5 py-4 text-left font-display text-base font-extrabold uppercase tracking-[0.1em] transition-[border-color,background-color] ${
                                form.interest === i ? 'border-[#C92F4B] bg-[#C92F4B]/5 text-[#A41225]' : 'border-[#E4E6EB] text-[#1C1D21]'
                              }`}
                            >
                              {i}
                              <span className={`h-4 w-4 rounded-full border-2 ${form.interest === i ? 'border-[#C92F4B] bg-[#C92F4B]' : 'border-[#A7ABB0]'}`} />
                            </button>
                          ))}
                        </div>
                        {errors.interest && <p data-testid="enquiry-interest-error" className="mt-2 text-xs font-semibold text-[#A41225]">{errors.interest}</p>}
                        <p className="mt-6 text-sm font-bold text-[#1C1D21]">Furnishing preference <span className="font-semibold text-[#646A72]">(optional)</span></p>
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          {ENQUIRY.furnishings.map((f) => (
                            <button
                              type="button"
                              key={f} data-testid={`enquiry-furnishing-${f.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                              onClick={() => set('furnishing', form.furnishing === f ? '' : f)}
                              aria-pressed={form.furnishing === f}
                              className={`min-h-[48px] rounded-xl border px-3 py-2.5 text-xs font-bold transition-[border-color,background-color] ${
                                form.furnishing === f ? 'border-[#C92F4B] bg-[#C92F4B]/5 text-[#A41225]' : 'border-[#E4E6EB] text-[#404145]'
                              }`}
                            >
                              {f}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {step === 2 && (
                      <div>
                        <p className="text-sm font-bold text-[#1C1D21]">When are you planning to buy?</p>
                        <div className="mt-4 grid gap-3">
                          {ENQUIRY.timelines.map((t) => (
                            <button
                              type="button"
                              key={t} data-testid={`enquiry-timeline-${t.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                              onClick={() => set('timeline', t)}
                              aria-pressed={form.timeline === t}
                              className={`flex min-h-[52px] items-center justify-between rounded-xl border px-5 py-3.5 text-left text-sm font-bold transition-[border-color,background-color] ${
                                form.timeline === t ? 'border-[#C92F4B] bg-[#C92F4B]/5 text-[#A41225]' : 'border-[#E4E6EB] text-[#1C1D21]'
                              }`}
                            >
                              {t}
                              <span className={`h-4 w-4 rounded-full border-2 ${form.timeline === t ? 'border-[#C92F4B] bg-[#C92F4B]' : 'border-[#A7ABB0]'}`} />
                            </button>
                          ))}
                        </div>
                        {errors.timeline && <p data-testid="enquiry-timeline-error" className="mt-2 text-xs font-semibold text-[#A41225]">{errors.timeline}</p>}
                      </div>
                    )}

                    {step === 3 && (
                      <div className="space-y-5">
                        <div>
                          <label htmlFor="enq-date" className="text-sm font-bold text-[#1C1D21]">Preferred visit date</label>
                          <input
                            id="enq-date" name="visit-date" data-testid="enquiry-date-input" type="date"
                            onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }), 320)}
                            min={new Date().toISOString().split('T')[0]}
                            value={form.visitDate} onChange={(e) => set('visitDate', e.target.value)}
                            className="mt-2 w-full rounded-xl border border-[#E4E6EB] bg-[#FAF9F6] px-4 py-3.5 text-base outline-none focus:border-[#C92F4B]"
                          />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-[#1C1D21]">Preferred time</p>
                          <div className="mt-3 grid gap-2">
                            {ENQUIRY.timeSlots.map((t) => (
                              <button
                                type="button"
                                key={t} data-testid={`enquiry-timeslot-${t.split(' ')[0].toLowerCase()}`}
                                onClick={() => set('visitTime', t)}
                                aria-pressed={form.visitTime === t}
                                className={`min-h-[48px] rounded-xl border px-4 py-3 text-sm font-bold transition-[border-color,background-color] ${
                                  form.visitTime === t ? 'border-[#C92F4B] bg-[#C92F4B]/5 text-[#A41225]' : 'border-[#E4E6EB] text-[#1C1D21]'
                                }`}
                              >
                                {t}
                              </button>
                            ))}
                          </div>
                          {errors.visitTime && <p data-testid="enquiry-time-error" className="mt-2 text-xs font-semibold text-[#A41225]">{errors.visitTime}</p>}
                        </div>
                      </div>
                    )}

                    {step === 4 && (
                      <div>
                        <div className="rounded-2xl border border-[#E4E6EB] bg-[#FAF9F6] p-5">
                          <p className="font-display text-[10px] font-bold uppercase tracking-[0.24em] text-[#A41225]">Your visit</p>
                          <dl className="mt-3 space-y-2 text-sm">
                            <div className="flex justify-between gap-4"><dt className="text-[#646A72]">Name</dt><dd className="font-bold text-[#1C1D21]">{form.name}</dd></div>
                            <div className="flex justify-between gap-4"><dt className="text-[#646A72]">Mobile</dt><dd className="font-bold text-[#1C1D21]">+91 {form.mobile.replace(/\D/g, '').slice(-10)}</dd></div>
                            <div className="flex justify-between gap-4"><dt className="text-[#646A72]">Interested in</dt><dd className="font-bold text-[#1C1D21]">{form.interest}</dd></div>
                            {form.furnishing && <div className="flex justify-between gap-4"><dt className="text-[#646A72]">Furnishing</dt><dd className="font-bold text-[#1C1D21]">{form.furnishing}</dd></div>}
                            <div className="flex justify-between gap-4"><dt className="text-[#646A72]">Timeline</dt><dd className="font-bold text-[#1C1D21]">{form.timeline}</dd></div>
                            <div className="flex justify-between gap-4"><dt className="text-[#646A72]">Visit</dt><dd className="font-bold text-[#1C1D21]">{form.visitDate || 'Flexible'} · {form.visitTime}</dd></div>
                          </dl>
                        </div>
                        <label className="mt-5 flex cursor-pointer items-start gap-3" data-testid="enquiry-consent-label">
                          <input
                            type="checkbox" data-testid="enquiry-consent-checkbox"
                            checked={form.consent} onChange={(e) => set('consent', e.target.checked)}
                            className="mt-0.5 h-5 w-5 shrink-0 accent-[#C92F4B]"
                          />
                          <span className="text-xs leading-relaxed text-[#404145]">
                            I agree to be contacted by Today Shyam Developers over call, SMS or WhatsApp regarding Codename Metro One — Navade (Aarohi Complex, MahaRERA P52000015331).
                          </span>
                        </label>
                        {errors.consent && <p data-testid="enquiry-consent-error" className="mt-2 text-xs font-semibold text-[#A41225]">{errors.consent}</p>}
                        {failed && (
                          <p data-testid="enquiry-submit-error" className="mt-3 rounded-xl bg-[#A41225]/8 px-4 py-3 text-xs font-semibold text-[#A41225]">
                            Submission didn’t go through — your details are saved here. Please retry, or call us directly.
                          </p>
                        )}
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              )}
            </div>

            {!done && (
              <div className="safe-bottom flex gap-3 border-t border-[#E4E6EB] px-6 py-4">
                {step > 0 && (
                  <button type="button" data-testid="enquiry-back-btn" onClick={back} className="flex min-h-[48px] items-center gap-1 rounded-full border border-[#E4E6EB] px-5 text-sm font-bold text-[#404145]">
                    <ArrowLeft size={15} /> Back
                  </button>
                )}
                {step < 4 ? (
                  <button type="submit" data-testid="enquiry-next-btn" className="btn-primary flex-1">Continue</button>
                ) : (
                  <button type="submit" data-testid="enquiry-submit-btn" disabled={submitting} className="btn-primary flex-1 disabled:opacity-60">
                    {submitting ? 'Submitting…' : 'Confirm Site Visit'}
                  </button>
                )}
              </div>
            )}
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
